import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useSyncExternalStore } from 'react'
import { useFamilyPortalApiClient } from '../features/auth/hooks/useFamilyPortalApiClient'
import type { ProfileNameParts } from '../utils/displayName'

type ProfileDemographics = {
  firstName?: string | null
  lastName?: string | null
  emailAddress?: string | null
}

type DemographicsClient = {
  get: (url: string) => Promise<{ ok: boolean; json: () => Promise<unknown> }>
}

export type ProfileNameState = ProfileNameParts & { isLoading: boolean }

type CacheEntry = { status: 'ready'; parts: ProfileNameParts } | { status: 'failed' }

const EMPTY_PROFILE: ProfileNameParts = { firstName: null, lastName: null, emailAddress: null }

// Module-level store so concurrent useProfileName() callers share one request and stay in sync.
// entry null = loading for the bound user; ready/failed = settled (retry clears failed → null).
let entry: CacheEntry | null = null
let cachedUserSub: string | null = null
let inflight: Promise<ProfileNameParts> | null = null
let generation = 0
let lastSnapshot: ProfileNameState | null = null
const listeners = new Set<() => void>()

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

function emit(): void {
  listeners.forEach((listener) => listener())
}

function partsFromEntry(): ProfileNameParts {
  return entry?.status === 'ready' ? entry.parts : EMPTY_PROFILE
}

function computeIsLoading(userSub: string | null): boolean {
  if (userSub === null) {
    return false
  }
  if (cachedUserSub !== userSub) {
    return true
  }
  return entry === null
}

function snapshotFor(userSub: string | null): ProfileNameState {
  const parts = cachedUserSub === userSub ? partsFromEntry() : EMPTY_PROFILE
  const next: ProfileNameState = {
    firstName: parts.firstName,
    lastName: parts.lastName,
    emailAddress: parts.emailAddress,
    isLoading: computeIsLoading(userSub),
  }
  if (
    lastSnapshot &&
    lastSnapshot.firstName === next.firstName &&
    lastSnapshot.lastName === next.lastName &&
    lastSnapshot.emailAddress === next.emailAddress &&
    lastSnapshot.isLoading === next.isLoading
  ) {
    return lastSnapshot
  }
  lastSnapshot = next
  return next
}

function bindCacheToUser(userSub: string | null): void {
  if (cachedUserSub === userSub) {
    return
  }
  generation += 1
  entry = null
  inflight = null
  cachedUserSub = userSub
  emit()
}

function writeReady(parts: ProfileNameParts): void {
  entry = { status: 'ready', parts }
  emit()
}

function writeFailed(): void {
  entry = { status: 'failed' }
  emit()
}

export function resetProfileNameCache(): void {
  generation += 1
  entry = null
  inflight = null
  cachedUserSub = null
  lastSnapshot = null
  emit()
}

/** Push a known profile name into the shared cache (e.g. after a successful demographics save). */
export function publishProfileName(parts: ProfileNameParts): void {
  generation += 1
  inflight = null
  writeReady(parts)
}

function loadProfileName(apiClient: DemographicsClient): Promise<ProfileNameParts> {
  if (entry?.status === 'ready') {
    return Promise.resolve(entry.parts)
  }
  if (!inflight) {
    // Remount / second subscriber after failure: clear settled-failed so isLoading is true again.
    if (entry?.status === 'failed') {
      entry = null
      emit()
    }
    const requestGeneration = generation
    inflight = apiClient
      .get('/profile/demographics')
      .then(async (res) => {
        if (!res.ok) {
          throw new Error('demographics request failed')
        }
        const data = (await res.json()) as ProfileDemographics
        return {
          firstName: data.firstName ?? null,
          lastName: data.lastName ?? null,
          emailAddress: data.emailAddress ?? null,
        }
      })
      .then((parts) => {
        if (requestGeneration !== generation) {
          return entry?.status === 'ready' ? entry.parts : EMPTY_PROFILE
        }
        inflight = null
        writeReady(parts)
        return parts
      })
      .catch(() => {
        if (requestGeneration === generation) {
          inflight = null
          writeFailed()
        }
        return EMPTY_PROFILE
      })
  }
  return inflight
}

/**
 * Fetches raw profile first/last name and email from IMDB demographics.
 * Concurrent callers share one in-flight request. No Auth0 fallback.
 */
export function useProfileName(): ProfileNameState {
  const { user } = useAuth0()
  const apiClient = useFamilyPortalApiClient()
  const userSub = user?.sub ?? null
  const profileName = useSyncExternalStore(subscribe, () => snapshotFor(userSub))

  useEffect(() => {
    bindCacheToUser(userSub)
    void loadProfileName(apiClient)
  }, [apiClient, userSub])

  return profileName
}
